<template>
  <div class="menu-button">
    <MenuButton buttonLabel="Settings" class="menu-button">
      <!-- The floating menu opens inside the page, where its no-access guard sees this click; a mobile sheet would not -->
      <MenuButtonItem
        v-if="canMoveIn"
        data-manage-action
        title="Move into a building"
        subtitle="Keeps the listing, leads and apply link"
        icon="building-06"
        @click="attachModalRef?.open(property?.id)"
      />
      <MenuButtonItem
        v-else-if="building"
        data-manage-action
        :title="`Move out of ${building.name}`"
        subtitle="Becomes a standalone home again"
        icon="building-06"
        @click="detachModalRef?.show()"
      />
      <MenuButtonItem
        data-manage-action
        @click="deleteProperty"
        title="Delete property"
        icon="trash"
        variant="dangerous"
      />
    </MenuButton>

    <PropertyDeleteModal ref="deleteModalRef" />
    <AttachHomesModal v-if="canMoveIn" ref="attachModalRef" @attached="reload" />
    <ConfirmModal
      v-if="building"
      ref="detachModalRef"
      :title="`Move ${unitName} out of ${building.name}?`"
      :description="detachDescription"
      confirm-label="Move out"
      :loading="isDetaching"
      width="26rem"
      @confirm="detach"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { ConfirmModal, MenuButton, MenuButtonItem, useAlertsStore } from '@tallo/design-system';
import { teamMemberName } from '@tallo/investor/company';
import { useBuildingQuery, useBuildingsQuery, useDetachUnit, usePropertyStore } from '@tallo/investor/property';
import { PropertyType } from '@tallo/property';
import { escapeHtml } from '@tallo/utility';

import AttachHomesModal from '../../building/components/attach-homes-modal.vue';
import PropertyDeleteModal from './property-delete-modal.vue';

const propertyStore = usePropertyStore();
const alertsStore = useAlertsStore();
const { mutateAsync: detachUnit } = useDetachUnit();

const deleteModalRef = ref<InstanceType<typeof PropertyDeleteModal>>();
const attachModalRef = ref<InstanceType<typeof AttachHomesModal>>();
const detachModalRef = ref<InstanceType<typeof ConfirmModal>>();
const isDetaching = ref(false);

const property = computed(() => propertyStore.property);
// Only a standing apartment can join a building
const isStandingApartment = computed(
  () =>
    Boolean(property.value) &&
    !property.value?.buildingId &&
    property.value?.specifications?.propertyType === PropertyType.APARTMENT,
);
const { buildings } = useBuildingsQuery(() => isStandingApartment.value);
const { data: building } = useBuildingQuery(() => property.value?.buildingId ?? '');

const canMoveIn = computed(() => isStandingApartment.value && buildings.value.length > 0);
const unitName = computed(() => {
  const number = property.value?.location?.apartmentNumber;

  return number ? `#${number}` : 'this unit';
});
const detachDescription = computed(() => {
  const home = property.value?.displayName ?? 'The unit';
  const portfolio = building.value?.portfolio?.name;
  const manager = building.value?.manager ? teamMemberName(building.value.manager) : undefined;
  const stays = [portfolio ? `in ${portfolio}` : '', manager ? `with ${manager} as manager` : '']
    .filter(Boolean)
    .join(' ');

  return (
    `${home} becomes a standalone home. It keeps its listing, leads, showings, applications and its current ` +
    `amenities, parking and access notes — they just stop following the building.` +
    (stays ? ` It stays ${stays}; change either afterwards from the home's page.` : '')
  );
});

function deleteProperty() {
  deleteModalRef.value?.modalRef?.show();
}

/** The unit's page reads its name, address and building from the store, which the move changed */
async function reload() {
  if (property.value) {
    await propertyStore.loadProperty(property.value.id);
  }
}

async function detach() {
  const unit = property.value;
  const home = building.value;

  if (!unit || !home) {
    return;
  }

  isDetaching.value = true;

  try {
    await detachUnit({ buildingId: home.id, propertyId: unit.id });
  } catch (error) {
    // Someone who can't change the building gets its access dialog, which holds this toast back
    alertsStore.catchError(error);
    return;
  } finally {
    isDetaching.value = false;
  }

  detachModalRef.value?.close();
  alertsStore.success(`<b>${escapeHtml(unit.displayName)}</b> stands on its own again`);
  await reload();
}
</script>
